import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { it as itLocale } from 'date-fns/locale';
import { toast } from 'sonner';
import { Loader2, Trash2, Mail, MailOpen } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { api } from '@/lib/api';
import { TEST_IDS } from '@/constants/testIds/floreal';
import { cn } from '@/lib/utils';

export default function MessagesPanel() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const res = await api.get('/admin/messages');
            setItems(res.data || []);
        } catch { toast.error('Impossibile caricare i messaggi'); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const markRead = async (id) => {
        try {
            await api.put(`/admin/messages/${id}/read`);
            load();
        } catch { toast.error('Errore aggiornamento'); }
    };

    const remove = async (id) => {
        if (!window.confirm('Eliminare questo messaggio?')) return;
        try {
            await api.delete(`/admin/messages/${id}`);
            toast.success('Messaggio eliminato');
            load();
        } catch { toast.error('Errore eliminazione'); }
    };

    return (
        <div className="rounded-xl border border-border bg-card overflow-hidden">
            <div className="px-5 py-4 border-b border-border flex items-center justify-between">
                <h2 className="font-serif text-xl">Messaggi dal form contatti</h2>
                <span className="text-xs text-muted-foreground">{items.filter((m) => !m.read).length} non letti · {items.length} totali</span>
            </div>
            <div className="overflow-x-auto">
                <Table data-testid={TEST_IDS.adminMessagesTable}>
                    <TableHeader>
                        <TableRow>
                            <TableHead className="w-10"></TableHead>
                            <TableHead>Da</TableHead>
                            <TableHead>Contatti</TableHead>
                            <TableHead>Messaggio</TableHead>
                            <TableHead>Quando</TableHead>
                            <TableHead className="text-right">Azioni</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading ? (
                            <TableRow><TableCell colSpan={6} className="py-10 text-center text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Caricamento…</TableCell></TableRow>
                        ) : items.length === 0 ? (
                            <TableRow><TableCell colSpan={6} className="py-10 text-center text-muted-foreground">Nessun messaggio al momento.</TableCell></TableRow>
                        ) : items.map((m) => (
                            <TableRow key={m.id} className={cn('cursor-pointer hover:bg-secondary/40', !m.read && 'bg-atelier-blush/10')} onClick={() => { setSelected(m); if (!m.read) markRead(m.id); }}>
                                <TableCell>{m.read ? <MailOpen className="h-4 w-4 text-muted-foreground" /> : <Mail className="h-4 w-4 text-atelier-sage-ink" />}</TableCell>
                                <TableCell><div className="font-medium">{m.name}</div>{!m.read && <Badge variant="outline" className="text-[10px] mt-1 bg-atelier-blush/50 border-atelier-blush">Nuovo</Badge>}</TableCell>
                                <TableCell><div className="text-xs text-muted-foreground">{m.email}</div>{m.phone && <div className="text-xs text-muted-foreground">{m.phone}</div>}</TableCell>
                                <TableCell className="max-w-md"><span className="text-sm text-muted-foreground line-clamp-2">{m.message}</span></TableCell>
                                <TableCell><span className="text-xs text-muted-foreground whitespace-nowrap">{(() => { try { return format(new Date(m.created_at), 'd MMM HH:mm', { locale: itLocale }); } catch { return m.created_at; } })()}</span></TableCell>
                                <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => remove(m.id)}><Trash2 className="h-4 w-4" /></Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>

            <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
                <DialogContent className="max-w-xl">
                    {selected && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="font-serif text-2xl">{selected.name}</DialogTitle>
                                <DialogDescription>
                                    {selected.email}{selected.phone ? ` · ${selected.phone}` : ''}
                                </DialogDescription>
                            </DialogHeader>
                            <div className="rounded-md border border-border bg-atelier-cream p-4 text-sm leading-relaxed whitespace-pre-line max-h-[50vh] overflow-y-auto">
                                {selected.message}
                            </div>
                            <div className="text-xs text-muted-foreground">
                                Ricevuto {(() => { try { return format(new Date(selected.created_at), 'd MMMM yyyy alle HH:mm', { locale: itLocale }); } catch { return selected.created_at; } })()}
                            </div>
                            <div className="flex gap-2 mt-2">
                                <Button asChild variant="outline" className="flex-1">
                                    <a href={`mailto:${selected.email}?subject=Re%3A%20la%20tua%20richiesta&body=Ciao%20${encodeURIComponent(selected.name)}%2C%0A%0A`}>Rispondi via email</a>
                                </Button>
                                {selected.phone && (
                                    <Button asChild variant="outline" className="flex-1">
                                        <a href={`tel:${selected.phone}`}>Chiama</a>
                                    </Button>
                                )}
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
